
import React from 'react';
import { Order, Driver, DeliveryProvider, SystemConfig, AppMode, Incident } from '../types';
import { SettingsModal } from './SettingsModal';
import { OpenDeliveryModal } from './OpenDeliveryModal';
import { StaffPinModal } from './StaffPinModal';
import { PasteInputModal } from './PasteInputModal';
import { EditOrderModal } from './EditOrderModal';
import { CentralAlert } from './CentralAlert';
import { IncidentModal } from './IncidentModal';
import { CashFlowModal } from './CashFlowModal';
import { OnboardingModal } from './OnboardingModal';

interface Props {
  // Config & State
  config: SystemConfig;
  setConfig: (config: SystemConfig) => void;
  drivers: Driver[];
  setDrivers: React.Dispatch<React.SetStateAction<Driver[]>>;
  providers: DeliveryProvider[];
  setProviders: (providers: DeliveryProvider[]) => void;
  orders: Order[];
  setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
  storeId: string | null; // v10.2: Pass-through para equipe
  
  // Visibility States
  modals: {
    settings: boolean;
    openDelivery: boolean;
    staffPin: boolean;
    paste: boolean;
    edit: boolean;
    incident: boolean;
    cashFlow: boolean;
    centralAlert: any;
  };
  
  // Active Entities
  activeOrder: Order | null;
  incidentOrder: Order | null;
  finishedOrders: Order[];
  
  // App context
  appMode: AppMode;
  
  // Close Handlers
  onClose: (modalName: string) => void;
  
  // Action Handlers
  onOrderParsed: (order: Order) => void;
  onOrderUpdate: (order: Order) => void;
  onOrderDelete: (id: string) => void;
  onAppModeChange: (mode: AppMode) => void;
  onIncidentSave: (orderId: string, incident: Incident) => void;
  onAddSangria: (driverId: string, amount: number) => void;
  onFinalizeDay: () => void;
}

export const GlobalModalRegistry: React.FC<Props> = ({
  config, setConfig, drivers, setDrivers, providers, setProviders, orders, setOrders, storeId,
  modals, activeOrder, incidentOrder, finishedOrders, appMode,
  onClose, onOrderParsed, onOrderUpdate, onOrderDelete, onAppModeChange, onIncidentSave, onAddSangria, onFinalizeDay
}) => {
  
  const isNewUser = config.storeName === "Minha Nova Operação";

  return (
    <>
      <OnboardingModal 
        isOpen={isNewUser}
        config={config}
        onSave={async (updated) => {
            await setConfig(updated);
        }}
      />

      <SettingsModal 
        isOpen={modals.settings} 
        onClose={() => onClose('settings')} 
        config={config} 
        setConfig={setConfig} 
        drivers={drivers} 
        setDrivers={setDrivers} 
        providers={providers} 
        setProviders={setProviders} 
        orders={orders} 
        setOrders={setOrders} 
        storeId={storeId || undefined}
      />

      <OpenDeliveryModal 
        isOpen={modals.openDelivery} 
        onClose={() => onClose('openDelivery')} 
        config={config} 
        setConfig={setConfig} 
      />

      <StaffPinModal 
        isOpen={modals.staffPin} 
        onClose={() => onClose('staffPin')} 
        onSuccess={() => {
          if (appMode === AppMode.DISPATCHER) {
            onAppModeChange(AppMode.DRIVER_LOGIN);
          } else {
            onAppModeChange(AppMode.DISPATCHER);
          }
        }} 
        targetPin="5665" 
      />

      <PasteInputModal 
        isOpen={modals.paste} 
        onClose={() => onClose('paste')} 
        onOrderParsed={onOrderParsed} 
        googleMapsApiKey={config.googleMapsApiKey} 
        defaultLat={config.businessLat} 
        defaultLng={config.businessLng} 
      />

      <EditOrderModal 
        isOpen={modals.edit} 
        onClose={() => onClose('edit')} 
        order={activeOrder} 
        onSave={onOrderUpdate} 
        onDelete={onOrderDelete} 
      />

      <CentralAlert 
        isOpen={modals.centralAlert.isOpen} 
        onClose={() => onClose('centralAlert')} 
        title={modals.centralAlert.title} 
        message={modals.centralAlert.message} 
        icon={modals.centralAlert.icon} 
        actions={modals.centralAlert.actions} 
      />

      <IncidentModal 
        isOpen={modals.incident} 
        onClose={() => onClose('incident')} 
        order={incidentOrder} 
        onSaveIncident={onIncidentSave} 
      />

      <CashFlowModal 
        isOpen={modals.cashFlow} 
        onClose={() => onClose('cashFlow')} 
        orders={finishedOrders} 
        drivers={drivers} 
        config={config} 
        onFinalizeDay={onFinalizeDay} 
        onAddSangria={onAddSangria} 
      />
    </>
  );
};
